import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Activity, BarChart3 } from 'lucide-react';
import { Badge } from './ui/badge';

const CryptoChart = ({ crypto }) => {
  const [timeRange, setTimeRange] = useState('24h');
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (crypto) {
      generateMockChartData();
    }
  }, [crypto, timeRange]);

  // Generate mock historical data based on current price and time range
  const generateMockChartData = () => {
    if (!crypto?.quote?.USD) return;
    
    const currentPrice = crypto.quote.USD.price;
    const change24h = crypto.quote.USD.percent_change_24h / 100;
    const points = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : timeRange === '30d' ? 720 : 8760;
    
    const data = [];
    const startPrice = currentPrice / (1 + change24h);
    
    for (let i = 0; i < points; i++) {
      const progress = i / points;
      const randomVariation = (Math.random() - 0.5) * 0.02;
      const trendValue = startPrice + (currentPrice - startPrice) * progress;
      const price = trendValue * (1 + randomVariation);
      
      let label;
      if (timeRange === '24h') {
        label = `${i}:00`;
      } else if (timeRange === '7d') {
        const day = Math.floor(i / 24);
        label = `Day ${day + 1}`;
      } else if (timeRange === '30d') {
        const day = Math.floor(i / 24);
        label = `Day ${day + 1}`;
      } else {
        const month = Math.floor(i / 730);
        label = `M${month + 1}`;
      }
      
      data.push({
        time: label,
        price: price,
        volume: Math.random() * currentPrice * 1000000
      });
    }
    
    setChartData(data);
  };

  if (!crypto) {
    return (
      <Card className="border-slate-200 dark:border-slate-800" data-testid="crypto-chart-empty">
        <CardContent className="flex items-center justify-center h-[600px]">
          <p className="text-slate-500 dark:text-slate-400">Select a cryptocurrency to view its chart</p>
        </CardContent>
      </Card>
    );
  }

  const quote = crypto.quote?.USD;
  if (!quote) return null;

  const isPositive = quote.percent_change_24h >= 0;
  
  const formatPrice = (price) => {
    if (price >= 1) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${price.toFixed(6)}`;
  };

  const formatLargeNumber = (num) => {
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toFixed(0)}`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatPrice(payload[0].value)}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{payload[0].payload.time}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="border" style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }} data-testid="crypto-chart-card">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <img
                src={`https://s2.coinmarketcap.com/static/img/coins/64x64/${crypto.id}.png`}
                alt={crypto.name}
                className="w-10 h-10"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                }}
              />
              <CardTitle className="text-2xl font-bold" style={{ color: '#0F172A' }} data-testid="chart-crypto-name">
                {crypto.name} ({crypto.symbol})
              </CardTitle>
              <Badge 
                variant={isPositive ? "default" : "destructive"}
                style={{ 
                  backgroundColor: isPositive ? '#DCFCE7' : '#FEE2E2',
                  color: isPositive ? '#16A34A' : '#DC2626'
                }}
              >
                {isPositive ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                {Math.abs(quote.percent_change_24h).toFixed(2)}%
              </Badge>
            </div>
            <motion.div
              key={quote.price}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-3xl font-bold"
              style={{ color: '#0F172A' }}
              data-testid="chart-crypto-price"
            >
              {formatPrice(quote.price)}
            </motion.div>
          </div>
          
          <Tabs value={timeRange} onValueChange={setTimeRange} data-testid="chart-time-range">
            <TabsList style={{ backgroundColor: '#F8FAFC' }}>
              <TabsTrigger value="24h" data-testid="time-range-24h">24H</TabsTrigger>
              <TabsTrigger value="7d" data-testid="time-range-7d">7D</TabsTrigger>
              <TabsTrigger value="30d" data-testid="time-range-30d">30D</TabsTrigger>
              <TabsTrigger value="1y" data-testid="time-range-1y">1Y</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        <motion.div
          key={timeRange}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={isPositive ? "#16A34A" : "#DC2626"} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={isPositive ? "#16A34A" : "#DC2626"} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.3} />
              <XAxis 
                dataKey="time" 
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                interval={Math.floor(chartData.length / 6)}
              />
              <YAxis 
                stroke="#64748b"
                fontSize={12}
                tickLine={false}
                tickFormatter={(value) => {
                  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
                  return `$${value.toFixed(0)}`;
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="price" 
                stroke={isPositive ? "#16A34A" : "#DC2626"}
                strokeWidth={3}
                fill="url(#colorPrice)"
                animationDuration={1000}
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="chart-stats">
          <div className="p-4 rounded-lg border" style={{ backgroundColor: '#F8FAFC', borderColor: '#E5E7EB' }}>
            <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#475569' }}>
              <DollarSign className="w-4 h-4" />
              Market Cap
            </div>
            <div className="text-lg font-bold" style={{ color: '#0F172A' }} data-testid="market-cap">
              {formatLargeNumber(quote.market_cap)}
            </div>
          </div>
          
          <div className="p-4 rounded-lg border" style={{ backgroundColor: '#F8FAFC', borderColor: '#E5E7EB' }}>
            <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#475569' }}>
              <Activity className="w-4 h-4" />
              24h Volume
            </div>
            <div className="text-lg font-bold" style={{ color: '#0F172A' }} data-testid="volume-24h">
              {formatLargeNumber(quote.volume_24h)}
            </div>
          </div>
          
          <div className="p-4 rounded-lg border" style={{ backgroundColor: '#F8FAFC', borderColor: '#E5E7EB' }}>
            <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#475569' }}>
              <BarChart3 className="w-4 h-4" />
              Circulating Supply
            </div>
            <div className="text-lg font-bold" style={{ color: '#0F172A' }} data-testid="circulating-supply">
              {(crypto.circulating_supply / 1e6).toFixed(2)}M
            </div>
          </div>
          
          <div className="p-4 rounded-lg border" style={{ backgroundColor: '#F8FAFC', borderColor: '#E5E7EB' }}>
            <div className="flex items-center gap-2 text-sm mb-1" style={{ color: '#475569' }}>
              <TrendingUp className="w-4 h-4" />
              7d Change
            </div>
            <div className="text-lg font-bold" style={{ color: quote.percent_change_7d >= 0 ? '#16A34A' : '#DC2626' }} data-testid="change-7d">
              {quote.percent_change_7d >= 0 ? '+' : ''}{quote.percent_change_7d.toFixed(2)}%
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CryptoChart;
